
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action } from "mobx";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, languageStore } from "../_rootStore";
import { ColorResult } from "react-color";

// MARK: Services
import ImageService from "../../services/ImageService";
import AddressService from "../../services/AddressService";

// MARK: Resources
import strings from "../../resources/strings";

export default class HotelStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();
	public addressService: AddressService = new AddressService();

	// LogoImages
	public logoHorizontalImageService: ImageService = new ImageService();
	public logoSquaredImageService: ImageService = new ImageService();
	public logoWhiteImageService: ImageService = new ImageService();

	// Control
	@observable public addressLoading: boolean = false;
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public hotel: api.Hotel | null = null;
	@observable public selectedHotel: api.Hotel | null = null;

	// Variables
	@observable public name: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public email: string = "";
	@observable public phone: string = "";
	@observable public slogan: string = "";
	@observable public omnibeesId: string = "";
	@observable public videoUrl: string = "";
	@observable public roomsDescription: string = "";

	// MetaTags
	@observable public metaTags: string[] = [];
	@observable public selectedMetaTag: string = "";

	// Colors
	@observable public primary: string = "";
	@observable public secondary: string = "";
	@observable public tertiary: string = "";
	@observable public background: string = "";
	@observable public textPrimary: string = "";
	@observable public textSecondary: string = "";
	@observable public textTertiary: string = "";

	// SocialMedia
	@observable public instagram: string = "";
	@observable public facebook: string = "";
	@observable public linkedin: string = "";

	// ReserveDisponibility
	@observable public start: number = 0;
	@observable public end: number = 0;

	@action
	public editHotel = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		uiStore.setCircularLoading(true);

		if (!this.imageService.singleImageUploader || !this.imageService.singleImageUploader.uploadedUncertainImage) {
			uiStore.showSnackbar(strings.hotel.edit.errors.imageNotFound);
			return;
		}
		if (!this.logoHorizontalImageService.singleImageUploader || !this.logoHorizontalImageService.singleImageUploader.uploadedUncertainImage) {
			uiStore.showSnackbar(strings.hotel.edit.errors.logoHorizontalNotFound);
			return;
		}
		if (!this.logoSquaredImageService.singleImageUploader || !this.logoSquaredImageService.singleImageUploader.uploadedUncertainImage) {
			uiStore.showSnackbar(strings.hotel.edit.errors.logoSquaredNotFound);
			return;
		}
		try {
			if (this.hotel) {
				const editedHotel: api.EditHotel = {
					language: languageStore.selectedLanguage,
					name: this.name,
					nick: this.nick,
					description: this.description,
					omnibeesId: this.omnibeesId,
					email: this.email,
					phone: this.phone,
					videoUrl: this.videoUrl,
					slogan: this.slogan,
					image: this.imageService.singleImageUploader.uploadedUncertainImage,
					socialMedia: {
						facebook: this.facebook,
						instagram: this.instagram,
						linkedin: this.linkedin,
					},
					colors: {
						primary: this.primary,
						secondary: this.secondary,
						tertiary: this.tertiary,
						background: this.background,
						textPrimary: this.textPrimary,
						textSecondary: this.textSecondary,
						textTertiary: this.textTertiary,
					},
					logos: {
						horizontal: this.logoHorizontalImageService.singleImageUploader.uploadedUncertainImage,
						squared: this.logoSquaredImageService.singleImageUploader.uploadedUncertainImage,
						white: this.logoWhiteImageService.singleImageUploader &&
						this.logoWhiteImageService.singleImageUploader.uploadedUncertainImage ?
							this.logoWhiteImageService.singleImageUploader.uploadedUncertainImage :
							null,
					},
					reserveDisponibility: {
						start: this.start,
						end: this.end,
					},
					roomsDescription: this.roomsDescription,
					address: this.addressService.address,
					metaTags: this.metaTags,
				};

			this.selectedHotel = await api.editHotel(editedHotel);
			uiStore.showSnackbar(strings.hotel.edit.saved);
				this.selectedHotel = await api.editHotel(editedHotel);
				uiStore.showSnackbar(strings.hotel.edit.saved);
			}
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
			uiStore.setCircularLoading(false);
		}
	};

	@action
	public getHotel = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		uiStore.setCircularLoading(true);

		try {
			this.hotel = await api.getAdminUserHotel();
			this.setEditorFields(this.hotel);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
			uiStore.setCircularLoading(false);
		}
	};

	@action
	public clear = () => {
		this.addressService.clear();
		this.imageService.clear();
		this.logoHorizontalImageService.clear();
		this.logoSquaredImageService.clear();
		this.logoWhiteImageService.clear();
		this.name = "";
		this.description = "";
		this.slogan = "";
		this.email = "";
		this.phone = "";
		this.omnibeesId = "";
		this.primary = "";
		this.secondary = "";
		this.tertiary = "";
		this.background = "";
		this.textPrimary = "";
		this.textSecondary = "";
		this.textTertiary = "";
		this.facebook = "";
		this.instagram = "";
		this.linkedin = "";
		this.omnibeesId = "";
		this.roomsDescription = "";
		this.start = 0;
		this.end = 0;
		this.videoUrl = "";
	};

	@action
	public setEditorFields = (hotel: api.Hotel) => {
		const hotelInSelectedLanguage = hotel.languages.filter((hotelLanguage) =>
		hotelLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setSingleImage(hotelInSelectedLanguage.image);
		this.logoHorizontalImageService.setSingleImage(hotel.logos.horizontal);
		this.logoSquaredImageService.setSingleImage(hotel.logos.squared);
		if (hotel.logos.white) {
			this.logoWhiteImageService.setSingleImage(hotel.logos.white);
		}
		this.addressService.setFields(hotel.address);
		this.name = hotelInSelectedLanguage.name;
		this.description = hotelInSelectedLanguage.description;
		this.slogan = hotelInSelectedLanguage.slogan ? hotelInSelectedLanguage.slogan : "";
		this.roomsDescription = hotelInSelectedLanguage.roomsDescription ? hotelInSelectedLanguage.roomsDescription : "";
		this.email = hotel.email;
		this.phone = hotel.phone;
		this.omnibeesId = hotel.omnibeesId;
		this.videoUrl = hotel.videoUrl;
		this.roomsDescription = hotelInSelectedLanguage.roomsDescription ? hotelInSelectedLanguage.roomsDescription : "";
		this.primary =  hotel.colors.primary;
		this.secondary =  hotel.colors.secondary;
		this.tertiary =  hotel.colors.tertiary ? hotel.colors.tertiary : "";
		this.background =  hotel.colors.background;
		this.textPrimary =  hotel.colors.textPrimary;
		this.textSecondary =  hotel.colors.textSecondary ? hotel.colors.textSecondary : "";
		this.textTertiary =  hotel.colors.textTertiary ? hotel.colors.textTertiary : "";
		this.facebook = hotel.socialMedia.facebook ? hotel.socialMedia.facebook : "" ;
		this.instagram = hotel.socialMedia.instagram ? hotel.socialMedia.instagram : "" ;
		this.linkedin = hotel.socialMedia.linkedin ? hotel.socialMedia.linkedin : "" ;
		this.start = hotel.reserveDisponibility.start;
		this.end = hotel.reserveDisponibility.end;
	};

	@action
	public handleColorChange = (name: string, color: ColorResult) => {
		this[name] = color.hex;
	}

	@action
	public addMetaTag = async () => {
		if (!this.metaTags.includes(this.selectedMetaTag) && this.selectedMetaTag !== "") {
			this.metaTags.push(this.selectedMetaTag);
		}
		this.closeMetaTagDialog();
	}

	@action
	public removeMetaTag = async (selectedMetaTag: string) => {
		this.metaTags = this.metaTags.filter((metaTag) => metaTag !== selectedMetaTag);
	}

	// MetaTag Dialog
	@observable
	public metaTagDialogOpen: boolean = false;

	@observable
	public metaTagDialogTitle: string = "";

	@action
	public openMetaTagDialog = () => {
		this.metaTagDialogOpen = true;
		this.metaTagDialogTitle = strings.hotel.edit.metaTagDialog.title;
	}
	@action
	public closeMetaTagDialog = () => {
		this.metaTagDialogOpen = false;
		this.selectedMetaTag = "";
	}
}
